import _ from "lodash"
// import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type Area = {
  propertyid: number
  areaid: number
}
// private module logic //

const id: string = "areaMeasures"
const initialState: ApiResult<Area[]> = { id, state: "init", value: [] }
const areaMeasuresStream = new BehaviorSubject<ApiResult<Area[]>>(initialState)
const areaMeasuresEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

let areaMeasuresMap = {}
const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  areaMeasuresStream.next({ id, state: 'ok', value: [] })
  areaMeasuresMap = {}
}

function get(areaid: number): Area {
  return areaMeasuresMap[areaid]
}

function refresh(): Promise<any> {
  const currentValue = areaMeasuresStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchAreaMeasures(propertyid)
}

function fetchAreaMeasures(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<Area[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  areaMeasuresStream.next({ id, state: 'busy', value: areaMeasuresStream.getValue().value })

  let url = `area?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `area?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: Area[]) => {
    _.forEach(records, (areaMeasures) => (areaMeasuresMap[areaMeasures.areaid] = areaMeasures))
    // merge results unique by areaid
    areaMeasuresStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(propertyid: number): Promise<Area | { message: string }> {
  const currentValue = areaMeasuresStream.getValue().value
  areaMeasuresStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    inputtype: 'basic',
    areadetail: {
      state: 'basic'
    }
  }

  return Client.post(`area`, payload)
    .then((area: Area) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, area.areaid),
        redofunc: () => create(propertyid)
      })
      areaMeasuresMap[area.areaid] = area
      // merge results unique by areaid
      const currentValue = areaMeasuresStream.getValue().value
      areaMeasuresStream.next({
        id,
        state: 'ok',
        value: _.unionBy([area], currentValue, 'areaid')
      })
      areaMeasuresEventStream.next({ type: 'AREAMEASURE_CREATED', payload: area })
      return area
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      areaMeasuresStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchAreaMeasures(propertyid)
      return err
    })
}

function copy(propertyid:number, areaid:number, code:string) {
  const currentValue = areaMeasuresStream.getValue().value;
  areaMeasuresStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'area', recordid: areaid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((areas: Area[]) => {
      const newRecord = areas[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.areaid),
        redofunc: () => copy(propertyid, areaid, code)
      });
      areaMeasuresMap[newRecord.areaid] = newRecord
      // merge results unique by areaid
      const currentValue = areaMeasuresStream.getValue().value
      areaMeasuresStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'areaid').sort((a, b) => alphanum(a.code, b.code))
      })
      areaMeasuresEventStream.next({ type: 'AREAMEASURE_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      areaMeasuresStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchAreaMeasures(propertyid)
      return err
    })
}

function copies(propertyid:number, areaid:number, count: number) {
  const currentValue = areaMeasuresStream.getValue().value;
  areaMeasuresStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'area', recordid: areaid, _synchronous: true, count, unique: true};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((areas: Area[]) => {
      push_undo({
        undofunc: () => deleteMultiple(propertyid, _.map(areas, 'areaid')),
        redofunc: () => copies(propertyid, areaid, count)
      });
      areas.forEach(area => areaMeasuresMap[area.areaid] = area)
      // merge results unique by areaid
      const currentValue = areaMeasuresStream.getValue().value
      areaMeasuresStream.next({
        id,
        state: 'ok',
        value: _.unionBy(areas, currentValue, 'areaid').sort((a, b) => alphanum(a.code, b.code))
      })
      areaMeasuresEventStream.next({ type: 'AREAMEASURE_COPIED', payload: areas })
      return areas
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      areaMeasuresStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchAreaMeasures(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  areaid: number
): Promise<boolean | { message: string }> {
  const currentValue = areaMeasuresStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, areaid)
  const refreshFn = () => fetchAreaMeasures(propertyid)
  if (!(await handleReferences('area', areaid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted areaid
  const value = _(currentValue)
    .filter((p) => p.areaid !== areaid)
    .value()
  value.forEach((p) => (areaMeasuresMap[p.areaid] = p))

  areaMeasuresStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(areaid).then(() =>
  Client.del(`area?areaid=eq.${areaid}`)
    .then((areas: Area[]) => {
      areas.forEach(a => delete areaMeasuresMap[a.areaid])
      push_undo({
        undofunc: () => {
          Client.post('area', areas)
          areas.forEach(a => areaMeasuresMap[a.areaid] = a)
          areaMeasuresStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, areaid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      areaMeasuresStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchAreaMeasures(propertyid)
      return err
    })
  )
}

async function deleteMultiple(
  propertyid: number,
  areaids: number[]
): Promise<boolean | { message: string }> {
  const currentValue = areaMeasuresStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(propertyid, ids)
  const refreshFn = () => fetchAreaMeasures(propertyid)
  if (!(await handleReferences('area', areaids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted areaid
  const value = _(currentValue)
  .filter((p) => !areaids.includes(p.areaid))
  .value()
  value.forEach((p) => (areaMeasuresMap[p.areaid] = p))

  areaMeasuresStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(areaids).then(() =>
  Client.del(`area?areaid=in.${areaids}`)
    .then((areas: Area[]) => {
      areas.forEach(area => delete areaMeasuresMap[area.areaid])
      push_undo({
        undofunc: () => {
          Client.post('area', areas)
          areas.forEach(area => areaMeasuresMap[area.areaid] = area)
          areaMeasuresStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteMultiple(propertyid, areaids)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      areaMeasuresStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchAreaMeasures(propertyid)
      return err
    })
  )
}


function patch(area: Area): Promise<object | { message: string }> {
  const currentValue = areaMeasuresStream.getValue().value
  const areaid = area.areaid
  const currentareaMeasures = _.find(currentValue, { areaid })
  const changeset = diff_obj(area, currentareaMeasures)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentareaMeasures),
    redofunc: () => patch(area)
  })

  // optimistic update
  areaMeasuresStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, { areaid }), area)
  })

  return _patches_.watch(areaid, Client.patch(`area?areaid=eq.${areaid}`, changeset)
    .then((areas: Area[]) => {
      _.forEach(areas, (a) => (areaMeasuresMap[a.areaid] = a))
      return areas[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      areaMeasuresStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentareaMeasures, 'propertyid')
      if (propertyid) fetchAreaMeasures(propertyid)
      return err
    }))
}

// public module interface //

const areaMeasuresService = {
  stream: () => areaMeasuresStream.asObservable(),
  value: () => areaMeasuresStream.getValue().value,
  eventStream: () => areaMeasuresEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  deleteMultiple,
  get,
  fetchAreaMeasures,
  patch,
  refresh
}

export default areaMeasuresService

